import { deepSignal } from "deepsignal";
import { InfoCircle, Pause, Play } from "flowbite-react-icons/solid";
import { useCallback, useEffect, useState } from "preact/hooks";
import Button from "@/components/buttons/button.jsx";
import NumberSliderInput from "@/components/input/number-slider.input.jsx";
import TextArea from "@/components/input/text-area.input.jsx";
import YesNoInput from "@/components/input/yes-no.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import SendTestDonation from "@/components/overlays/shared/send-test-donation.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import DevCustomizeOptions from "@/components/shared/dev-customize-options.jsx";
import useCommon from "@/hooks/useCommon.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";
import TTSPollSetup from "./tts-poll-setup.jsx";
import TTSVoicePicker from "./tts-voice-picker.jsx";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  pitch: 1,
  selectedVoiceIndex: 0,
  volume: 100,
  selectedVoice: "",
  pollID: "",
  usePoll: "no",
  autoStart: "yes",
  pollMapping: "",
  debugMode: "",
  showAllDonations: "",
  sampleText:
    'Some Cool Person donated $5.00 and said: "Keep up the good work! Lets hit that goal!"',
});

export default function TextToSpeechPage() {
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { overlayURL, setConfigState, overlayInfo, tiltifyConfig, setConfigKeyValue } = useCommon({
    config,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/tts" : "tts",
  });

  const sampleText = `${config.sampleText}`;
  let playTimeout = null;

  const synth = window.speechSynthesis;
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPolls, setHasPolls] = useState(false);
  const [hasLoadedPolls, setHasLoadedPolls] = useState(false);
  const [showPollMapping, setShowPollMapping] = useState(isYes(config.usePoll));
  const [volume, setVolume] = useState(100);

  const handlePollMappingChanged = useCallback(
    (compressedString) => {
      setConfigKeyValue("pollMapping", compressedString);
    },
    [setConfigKeyValue],
  );

  useEffect(() => {
    const fixedVolume = volume / 100;
    setConfigKeyValue("volume", fixedVolume);
  }, [volume, setConfigKeyValue]);

  useEffect(() => {
    if (!isYes(config.usePoll)) {
      tiltifyConfig.tiltifyPollID = "";
    }
  }, [tiltifyConfig]);

  useEffect(() => {
    config.pollID = tiltifyConfig.tiltifyPollID;
    setConfigKeyValue("pollID", tiltifyConfig.tiltifyPollID);
  }, [tiltifyConfig.tiltifyPollID, setConfigKeyValue]);

  useEffect(() => {
    if (isYes(config.usePoll) && !hasPolls && hasLoadedPolls) {
      setConfigKeyValue("usePoll", "no");
    } else if (!isYes(config.usePoll) && hasLoadedPolls) {
      tiltifyConfig.tiltifyPollID = "";
    }
  }, [hasPolls, hasLoadedPolls, setConfigKeyValue, tiltifyConfig]);

  useEffect(() => {
    setShowPollMapping(isYes(config.usePoll));
  }, []);

  function resetPolls() {
    tiltifyConfig.tiltifyPollID = "";
  }

  function handleVoiceSelection({ selectedVoice, selectedVoiceIndex }) {
    setConfigKeyValue("selectedVoice", selectedVoice.name);
    setConfigKeyValue("selectedVoiceIndex", selectedVoiceIndex);
  }

  function pauseSample() {
    synth.pause();
    setIsPlaying(false);
  }

  function playSample() {
    const utterance = new SpeechSynthesisUtterance(sampleText);
    utterance.voice = window.speechSynthesis.getVoices()[config.selectedVoiceIndex];
    utterance.pitch = config.pitch;
    utterance.onend = () => {
      setIsPlaying(false);
    };

    if (synth.speaking) {
      synth.cancel();
    }

    setIsPlaying(true);

    if (playTimeout !== null) {
      clearTimeout(playTimeout);
    }

    playTimeout = setTimeout(() => {
      synth.speak(utterance);
    }, 350);
  }

  const onPollsFetched = useCallback((polls) => {
    setHasPolls(polls.length > 0);
    setHasLoadedPolls(true);
  }, []);

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col items-start justify-center overflow-x-hidden">
        <div className="mb-4 w-full">
          <CustomizeSection title="Settings">
            <div role="alert" className="alert alert-warning my-4">
              <div className="flex flex-col items-start h-full">
                <InfoCircle />
              </div>
              <span className="text-xs">
                OBS uses Chrome internally for the Browser source, for best results, please use
                Chrome or Firefox to setup the overlay.
                <br /> <br />
                Additionally, whichever machine you use to setup the overlay should also be the
                machine you use to run the overlay, as the voice options may differ from machine to
                machine.
              </span>
            </div>

            <div className="my-2">
              <TTSVoicePicker
                label="Selected Voice"
                disabled={isYes(config.usePoll)}
                onVoiceSelected={handleVoiceSelection}
                currentVoiceName={config.selectedVoice}
              />
            </div>

            <NumberSliderInput
              fieldName="pitch"
              maxValue={2}
              minValue={0.1}
              step={0.1}
              labelText="Pitch"
              onChange={setConfigState}
              value={config.pitch}
            />

            <NumberSliderInput
              fieldName="volume"
              className="my-6"
              maxValue={100}
              minValue={1}
              step={1}
              labelText="Volume"
              onChange={(e) => setVolume(e.target.valueAsNumber)}
              value={volume}
              suffix="%"
            />

            <YesNoInput
              currentValue={config.autoStart}
              name="autoStart"
              onChange={setConfigState}
              mode="checkbox"
              label="Automatically start reading messages"
            >
              <span className="text-xs text-gray-500">
                {" "}
                Note: Auto-play does <i>not</i> work properly outside of the OBS Browser
              </span>
            </YesNoInput>
          </CustomizeSection>

          <CustomizeSection title="Preview" className="mb-4">
            <TextArea
              fieldName="sampleText"
              onChange={setConfigState}
              labelText="Sample Text"
              currentValue={config.sampleText}
            />

            <div className="mt-5 mb-5 flex flex-row">
              <Button type="circle" mode="button" onClick={playSample} disabled={isPlaying}>
                <Play />
              </Button>

              <Button
                type="circle"
                mode="button"
                style="warning"
                className="ml-2"
                onClick={pauseSample}
                disabled={!isPlaying}
              >
                <Pause />
              </Button>
            </div>
          </CustomizeSection>

          <DevCustomizeOptions setConfigState={setConfigState} config={config} />

          <LinkSetup
            charityType={config.charityType}
            overlayURL={overlayURL}
            overlayName={overlayInfo.name}
            obsDimensions={overlayInfo.obsDimensions}
            showTiltifyPollSelector={isYes(config.usePoll)}
            pollSelected={({ id }) => setConfigKeyValue("pollID", id)}
            childrenSlot={"stepThree"}
            childrenSlotPosition={"after"}
            pollsFetched={onPollsFetched}
            tiltifyCampaignSelected={() => resetPolls()}
            campaignStepContent={
              <div className={`collapse ${hasPolls ? "collapse-open" : "collapse-close"}`}>
                <div className="collapse-content p-0 mt-2">
                  <YesNoInput
                    currentValue={config.usePoll}
                    name="usePoll"
                    onChange={setConfigState}
                    mode="checkbox"
                    label="Use poll to control voice"
                  />
                </div>
              </div>
            }
            finalStepSlotPosition={"after"}
            finalStepContent={
              <div>
                <div className="my-2">
                  <b>Note: Make sure you do not have 'Control Audio via OBS' checked</b>
                </div>
                <SendTestDonation
                  donationType="message"
                  instructions="After you have added your browser source link to your streaming software, enter an amount and send a test announcement"
                  sendButtonText="Send Donation"
                />
              </div>
            }
          >
            <div className={`collapse ${showPollMapping ? "collapse-open" : "collapse-close"}`}>
              <div className="collapse-content p-0 mt-2">
                <TTSPollSetup
                  pollMappingChanged={handlePollMappingChanged}
                  pollID={config.pollID || tiltifyConfig.tiltifyPollID}
                />
              </div>
            </div>
          </LinkSetup>
        </div>
      </div>
    </div>
  );
}
